import React, { useCallback, useEffect, useMemo, useState } from "react";
import phone from "phone";
import { z } from "zod";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCreditCard } from "@fortawesome/pro-regular-svg-icons";
import { StripeExpressCheckoutElementConfirmEvent, StripeExpressCheckoutElementReadyEvent, StripeExpressCheckoutElementClickEvent } from "@stripe/stripe-js";
import { ExpressCheckoutElement } from "@stripe/react-stripe-js";
import StripePaymentElement from "./StripePaymentElement";
import StripeUtils from "./StripeUtils";
import { Divider } from "@/shared/components/ui/Divider";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { FieldPath, useFormContext } from "react-hook-form";
import { Quote, StripeExpressPaymentSchema } from "@/shared/types/Quote.interface";
import { AllBillingStepProps } from "../BillingEditor";
import { UIUtils } from "@/shared/utils/UIUtils";
import { cn } from "@/shared/utils";
import { Button } from "@/shared/components/ui";
import { AnalyticsUtils } from "@/shared/utils/AnalyticsUtils";
import Strings from "@/shared/utils/Strings.constants";
import { ApplePayOption } from "@stripe/stripe-js/dist/stripe-js/elements/apple-pay";
import { useFormMethodsContext } from "@/shared/components/Form";
import { useFeatures } from "@/shared/hooks/useFeatures";
import { getSchemaById } from "@/shared/components/FeatureSlot/schemaRegistry";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { UsQuoteFormSteps } from "@/quote-ptz-us/formConfigTypes";

interface PaymentChoiceFlowProps {
    quote: Quote;
    hideWalletPayment?: boolean; // Control wallet payment visibility
}

export default function PaymentChoiceFlow(props: PaymentChoiceFlowProps) {
    const [hasExpressPaymentMethod, setHasExpressPaymentMethod] = useState(false);
    const [expressCheckoutReady, setExpressCheckoutReady] = useState(false);
    const { appState, updateAppState } = useAppLayerContext();
    const { onFormContinue } = useFormMethodsContext() || {};
    const { showCreditCardFields, isQuoteUpdating } = appState;
    const { getValues, setValue, setError, setFocus, watch } = useFormContext<AllBillingStepProps>();
    const currentPhone = watch("phone");
    const { quote, hideWalletPayment = false } = props;

    const { getStepComponents } = useFeatures({
        formId: appState.formID,
        quote
    });

    // `hasExpressPaymentMethod` is measured by the Express Checkout element's `onReady`; hiding the
    // wallet suppresses it. That suppression is derived rather than written back into the state, so the
    // effect below is left with only its AppLayer sync.
    const hasVisibleExpressPaymentMethod = !hideWalletPayment && hasExpressPaymentMethod;

    useEffect(() => {
        if (hideWalletPayment) {
            updateAppState({
                showCreditCardFields: true,
                showBillingSubmitButton: true,
                stripeExpressPaymentType: undefined,
                stripeAvailablePaymentMethods: undefined
            });
        }
    }, [hideWalletPayment, updateAppState]);

    // Fallback: if express checkout never loads, show CC fields
    useEffect(() => {
        if (hideWalletPayment || expressCheckoutReady) return;
        const timer = setTimeout(() => {
            setExpressCheckoutReady(true);
            updateAppState({ showCreditCardFields: true, showBillingSubmitButton: true });
        }, 8000);
        return () => clearTimeout(timer);
    }, [hideWalletPayment, expressCheckoutReady, updateAppState]);

    const combinedSchema = useMemo(() => {
        const components = getStepComponents(UsQuoteFormSteps.BILLING.id);

        if (components.length === 0) {
            return StripeExpressPaymentSchema;
        }

        const featureSchemas = components
            .filter(component => component.schemaId)
            .map(component => getSchemaById(component.schemaId!))
            .filter(Boolean) // Remove null values
            .filter(schema => schema instanceof z.ZodObject); // Ensure ZodObject instances

        if (featureSchemas.length === 0) {
            return StripeExpressPaymentSchema;
        }

        // Merge feature schemas together first
        const combinedFeatureSchema = featureSchemas.reduce((combined, schema) => combined.merge(schema), z.object({}));

        // Then merge with base schema
        return StripeExpressPaymentSchema.merge(combinedFeatureSchema);
    }, [getStepComponents]);

    // Click pay on wallet modal
    const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
        AnalyticsUtils.sendDataDogAction("Stripe Express Checkout Confirm Clicked");
        const { billingDetails } = event;
        let zipCodeValue = billingDetails?.address.postal_code ?? "";
        if (zipCodeValue.length > 5) {
            zipCodeValue = zipCodeValue.slice(0, 5);
        }

        setValue("billingInfo.address.city", UIUtils.truncateString(billingDetails?.address.city ?? "", 50, false));
        setValue("billingInfo.address.state", billingDetails?.address.state ?? "");
        setValue("billingInfo.address.country", billingDetails?.address.country ?? "");
        setValue("billingInfo.address.zipCode", zipCodeValue);
        setValue("billingInfo.address.street1", UIUtils.truncateString(billingDetails?.address.line1 ?? "", 50, false));
        setValue("billingInfo.address.street2", UIUtils.truncateString(billingDetails?.address.line2 ?? "", 50, false));

        if (!currentPhone) {
            const formattedPhone = phone(billingDetails?.phone ?? "");
            const transformedPhone = UIUtils.transformPhoneNumber(formattedPhone.phoneNumber ?? "");
            setValue("phone", transformedPhone);
        }

        if (!!onFormContinue) {
            try {
                await onFormContinue();
            } catch (error) {
                console.error("Express Payment Error: ", error);
                event.paymentFailed();
            }
        } else {
            console.error("onFormContinue is not available");
            event.paymentFailed();
        }
    };

    const onExpressCheckoutLoad = (event: StripeExpressCheckoutElementReadyEvent) => {
        if (hideWalletPayment) return;

        setExpressCheckoutReady(true);

        const { availablePaymentMethods } = event;

        if (availablePaymentMethods) {
            const hasPaymentMethod = Object.values(availablePaymentMethods).some(value => !!value);
            setHasExpressPaymentMethod(hasPaymentMethod);
            updateAppState({ showCreditCardFields: false, showBillingSubmitButton: false, stripeAvailablePaymentMethods: availablePaymentMethods });
        } else {
            updateAppState({ showCreditCardFields: true, showBillingSubmitButton: true });
        }
    };

    const onExpressCheckoutElementClick = useCallback(
        (event: StripeExpressCheckoutElementClickEvent) => {
            if (hideWalletPayment || isQuoteUpdating) return; // part of Statsig test
            updateAppState({ showCreditCardFields: false, showBillingSubmitButton: false });

            const { expressPaymentType } = event;
            const currentValues = getValues();
            const result = combinedSchema.safeParse(currentValues);

            if (!result.success) {
                // Set all error messages first
                result.error.errors.forEach(err => {
                    const fieldName = err.path.join(".") as FieldPath<typeof currentValues>;
                    setError(fieldName, {
                        type: "manual",
                        message: err.message
                    });
                });

                // Handle scrolling based on the first error
                const firstErrorPath = result.error.errors[0]?.path.join(".");

                // Use a short delay to allow UI to update before scrolling
                setTimeout(() => {
                    // iOS-friendly scroll for the terms checkbox
                    if (firstErrorPath === "extra.termsConsent") {
                        const termsElement = document.getElementById("terms-consent");
                        if (termsElement) {
                            const elementRect = termsElement.getBoundingClientRect();
                            const absoluteElementTop = elementRect.top + window.scrollY;
                            const middle = absoluteElementTop - window.innerHeight / 2 + elementRect.height / 2;
                            window.scrollTo({ top: middle, behavior: "smooth" });
                        }
                    } else if (firstErrorPath) {
                        // For any other error, use the original setFocus method which works for standard inputs
                        setFocus(firstErrorPath as FieldPath<AllBillingStepProps>);
                    }
                }, 100); // delay to ensure UI has updated

                return; // Stop the click event
            }

            if (expressPaymentType) {
                updateAppState({ stripeExpressPaymentType: expressPaymentType, showBillingSubmitButton: true });
            }
            AnalyticsUtils.sendDataDogAction("Stripe Express Checkout Clicked", { expressPaymentType });

            let applePay: ApplePayOption | undefined;

            if (expressPaymentType === "apple_pay") {
                applePay = StripeUtils.convertQuoteToApplePayRecurringRequest(quote);
            }

            event.resolve({ phoneNumberRequired: !currentPhone, applePay: applePay });
        },
        [combinedSchema, currentPhone, getValues, isQuoteUpdating, quote, setError, setFocus, updateAppState, hideWalletPayment]
    );

    const onExpressCheckoutCancel = () => {
        if (hideWalletPayment) return; // part of Statsig test
        updateAppState({ showCreditCardFields: false, showBillingSubmitButton: false, stripeExpressPaymentType: undefined });
        AnalyticsUtils.sendDataDogAction("Stripe Express Checkout Cancelled");
    };

    const conditionalExpressClasses = {
        "my-5": hasVisibleExpressPaymentMethod,
        "opacity-50": isQuoteUpdating
    };

    return (
        <>
            {!hideWalletPayment && (
                <div id="express-checkout-element" data-testid="stripe-express-element" className={cn(conditionalExpressClasses)}>
                    <ExpressCheckoutElement
                        options={StripeUtils.expressPaymentOptions}
                        onReady={onExpressCheckoutLoad}
                        onClick={onExpressCheckoutElementClick}
                        onConfirm={onConfirm}
                        onCancel={onExpressCheckoutCancel}
                    />
                </div>
            )}
            {!hideWalletPayment && !expressCheckoutReady && !showCreditCardFields && (
                <div className="my-4 space-y-4" data-testid="payment-choice-skeleton">
                    <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-2">
                            <Skeleton className="h-8 w-full rounded bg-background-tertiary" />
                        </div>
                        <div className="space-y-2">
                            <Skeleton className="h-8 w-full rounded bg-background-tertiary" />
                        </div>
                        <div className="space-y-2">
                            <Skeleton className="h-8 w-full rounded bg-background-tertiary" />
                        </div>
                    </div>
                    <Skeleton className="h-10 w-full rounded bg-background-tertiary" />
                </div>
            )}
            {hasVisibleExpressPaymentMethod && <Divider color="muted">{Strings.PAYMENT_CHOICE}</Divider>}
            {(hideWalletPayment || expressCheckoutReady) &&
                (hideWalletPayment || showCreditCardFields ? (
                    <StripePaymentElement quote={quote} />
                ) : (
                    <Button
                        data-testid="show-cc-fields"
                        variant="default-rounded"
                        className={cn("mt-5")}
                        startDecorator={<FontAwesomeIcon icon={faCreditCard} className="size-5" />}
                        onClick={() => updateAppState({ showCreditCardFields: true, showBillingSubmitButton: true, stripeExpressPaymentType: undefined, asyncErrors: [] })}
                    >
                        {Strings.CREDIT_DEBIT_CARD}
                    </Button>
                ))}
        </>
    );
}
